/* Boonsoir 2.0 — Momenten on one continuous paper surface.
   - Marks the moment that matches the visitor's time (Europe/Amsterdam) with "Nu" (or "Straks" before the first one).
   - Sun travel is CSS scroll-driven where supported; otherwise (or with reduced motion) this script feeds --p.
   - Reduced motion: the sun simply rests on the current moment. */
(() => {
  'use strict';
  if (window.BoonsoirMoments) return;

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 749px)');
  const cssDriven = window.CSS && CSS.supports('animation-timeline: view()') && CSS.supports('timeline-scope: --a');
  const fmt = new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const clamp = (v) => Math.max(0, Math.min(1, v));

  function nowMinutes() {
    const p = {};
    fmt.formatToParts(new Date()).forEach((x) => { p[x.type] = x.value; });
    return (+p.hour % 24) * 60 + +p.minute;
  }
  const DAY_START = 6 * 60; // the evening runs on until 05:59
  const toMin = (s) => { const m = /^(\d{1,2})[:.](\d{2})/.exec(s || ''); return m ? +m[1] * 60 + +m[2] : null; };
  const shift = (min) => (min < DAY_START ? min + 1440 : min);

  class Moments {
    constructor(root) {
      this.root = root;
      this.items = [...root.querySelectorAll('[data-moment]')];
      this.rail = root.querySelector('.moments__list');
      this.line = root.querySelector('.moments__item:first-child .moments__time');
      this.last = Math.max(1, this.items.length - 1);
      this.moon = (+root.dataset.moon || this.last) / this.last;
      this.raf = 0;
      this.mark();
      this.timer = setInterval(() => this.mark(), 60000);

      this.onScroll = () => { if (!this.raf && this.inView) this.raf = requestAnimationFrame(() => this.measure()); };
      this.onMedia = () => this.setMode();
      mobile.addEventListener('change', this.onMedia);
      this.setMode();
    }

    // Desktop: CSS scroll-driven where supported. Mobile rail: always this script, so the sun rests exactly
    // on the dot of the moment that has snapped into place (a linear CSS timeline can't hit every snap point).
    setMode() {
      // In the evening the sun/moon rests on the current moment, as in reduced motion.
      const evening = document.documentElement.getAttribute('data-daypart') === 'evening';
      const mode = reduced.matches || evening ? 'static' : cssDriven && !mobile.matches ? 'css' : 'js';
      if (mode === this.mode) return;
      this.mode = mode;
      this.root.classList.toggle('moments--js', mode !== 'css');
      if (mode === 'static') this.restOnNow();
      if (mode === 'js') { this.follow(); this.measure(); } else this.unfollow();
    }

    /* "Nu" / "Straks" */
    mark() {
      const now = shift(nowMinutes());
      let idx = -1;
      this.items.forEach((el, i) => { const m = toMin(el.dataset.moment); if (m != null && shift(m) <= now) idx = i; });
      this.nowIdx = idx;
      this.items.forEach((el, i) => {
        const badge = el.querySelector('.moments__badge');
        const isNow = i === idx;
        const isSoon = idx === -1 && i === 0;
        el.classList.toggle('is-now', isNow);
        if (!badge) return;
        badge.hidden = !(isNow || isSoon);
        badge.textContent = isNow ? this.root.dataset.labelNow || 'Nu' : isSoon ? this.root.dataset.labelSoon || 'Straks' : '';
        badge.classList.toggle('badge--line', isSoon);
      });
      if (this.mode === 'static') this.restOnNow();
    }

    restOnNow() {
      const p = this.nowIdx > 0 ? this.nowIdx / this.last : 0;
      this.apply(p);
    }

    apply(p) {
      this.root.style.setProperty('--p', p.toFixed(4));
      this.root.classList.toggle('is-night', p >= this.moon - 0.02);
    }

    /* Fallback for browsers without scroll-driven animations: IntersectionObserver gates a rAF progress loop. */
    follow() {
      if (this.io) return;
      this.inView = true;
      this.io = new IntersectionObserver(([e]) => { this.inView = e.isIntersecting; this.onScroll(); }, { rootMargin: '10% 0px' });
      this.io.observe(this.root);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      if (this.rail) this.rail.addEventListener('scroll', this.onScroll, { passive: true });
    }

    unfollow() {
      if (!this.io) return;
      this.io.disconnect();
      this.io = null;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.rail) this.rail.removeEventListener('scroll', this.onScroll);
    }

    // Rail scroll → sun position, piecewise between snap points: resting on snap k puts the sun on dot k.
    railProgress() {
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      if (max <= 0) return 0;
      const rl = this.rail.getBoundingClientRect().left - this.rail.scrollLeft;
      const pad = parseFloat(getComputedStyle(this.rail).scrollPaddingLeft) || 0;
      const xs = this.items.map((el) => el.getBoundingClientRect().left - rl);
      const dots = xs.map((x) => x - xs[0]);
      const snaps = xs.map((x) => Math.max(0, Math.min(max, x - pad)));
      const sc = Math.max(0, Math.min(max, this.rail.scrollLeft));
      let k = 0;
      while (k < snaps.length - 2 && sc >= snaps[k + 1]) k += 1;
      const span = snaps[k + 1] - snaps[k];
      const f = span > 0 ? clamp((sc - snaps[k]) / span) : 1;
      const x = dots[k] + f * (dots[k + 1] - dots[k]);
      return clamp(x / dots[dots.length - 1]);
    }

    measure() {
      this.raf = 0;
      if (this.mode !== 'js') return;
      const vh = window.innerHeight;
      const r = this.line.getBoundingClientRect();
      // Mirrors the CSS range: sun over "cover 18%–58%" of the time row.
      const cover = (vh - r.top) / (vh + r.height);
      let p;
      if (mobile.matches && this.rail && this.items.length > 1) {
        p = this.railProgress();
      } else {
        p = clamp((cover - 0.18) / 0.4);
      }
      this.apply(p);
    }

    destroy() {
      clearInterval(this.timer);
      this.unfollow();
      if (this.raf) cancelAnimationFrame(this.raf);
      mobile.removeEventListener('change', this.onMedia);
    }
  }

  const instances = new Map();
  const init = (scope) => scope.querySelectorAll('[data-moments]').forEach((el) => { if (!instances.has(el)) instances.set(el, new Moments(el)); });
  const teardown = (scope) => instances.forEach((inst, el) => { if (scope.contains(el)) { inst.destroy(); instances.delete(el); } });

  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => teardown(e.target));
  window.BoonsoirMoments = true;
})();
